import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, Pencil, Trash2, Printer, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import { printTableWithHeader } from "../../../utils/printUtils";
import {
  genders,
  associateSchools,
  academicStatuses,
  professionalStatuses,
  getYearOptions,
} from "../../../utils/options";

import "./CurrentAlumniPage.css";

const PAGE_SIZE = 30;
const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const formatTime = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export default function CurrentAlumniPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    gender: "",
    year: "",
    associateSchool: "",
    academicStatus: "",
    professionalStatus: "",
  });

  const [alumni, setAlumni] = useState([]);
  const [serverTime, setServerTime] = useState({ fullDateTime: "" });
  const [currentUser, setCurrentUser] = useState({
    fullname: "",
    systemRole: "",
  });

  // Load current user
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setCurrentUser({
        fullname: user.fullname,
        systemRole: user.systemRole,
      });
    }
  }, []);

  // Fetch alumni & server time
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [alumniRes, timeRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/current-alumni/approved`),
          axios.get(`${API_BASE_URL}/time/datetime`),
        ]);
        setAlumni(alumniRes.data || []);
        if (timeRes.data.success) setServerTime(timeRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setTimeout(() => setLoading(false), 1200);
      }
    };
    fetchData();
  }, []);

  // Filtered + paginated alumni
  const filteredAlumni = useMemo(() => {
    return alumni.filter((a) => {
      const fullName = [a.title, a.firstName, a.middleName, a.lastName]
        .filter(Boolean)
        .join(" ");

      const matchesSearch =
        a.alumniID.toLowerCase().includes(search.toLowerCase()) ||
        a.email.toLowerCase().includes(search.toLowerCase()) ||
        fullName.toLowerCase().includes(search.toLowerCase());

      const matchesFilters =
        (!filters.gender || a.gender === filters.gender) &&
        (!filters.year || a.yearOfGraduation === Number(filters.year)) &&
        (!filters.associateSchool ||
          a.associateSchool === filters.associateSchool) &&
        (!filters.academicStatus ||
          a.academicStatus === filters.academicStatus) &&
        (!filters.professionalStatus ||
          a.professionalStatus === filters.professionalStatus);

      return matchesSearch && matchesFilters;
    });
  }, [search, filters, alumni]);

  const totalPages = Math.ceil(filteredAlumni.length / PAGE_SIZE);
  const paginatedData = filteredAlumni.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // Navigation handlers
  const handleView = (id) => navigate(`/admin-panel/alumni/view/${id}`);
  const handleEdit = (id) => navigate(`/admin-panel/alumni/edit/${id}`);
  const handleDelete = (id) => navigate(`/admin-panel/alumni/delete/${id}`);
  const handleGoToDeletedPage = () => navigate(`/admin-panel/deleted-alumni`);

  // ===============================
  // Print using printUtils
  // ===============================
  const handlePrint = () => {
    printTableWithHeader({
      title: "Current Alumni List",
      summary: "List of all approved alumni members",
      columns: [
        { key: "sn", label: "SN" },
        { key: "alumniID", label: "Alumni ID" },
        { key: "fullName", label: "Full Name" },
        { key: "gender", label: "Gender" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Telephone" },
      ],
      rows: filteredAlumni.map((a, idx) => ({
        sn: idx + 1,
        alumniID: a.alumniID,
        fullName: [a.title, a.firstName, a.middleName, a.lastName]
          .filter(Boolean)
          .join(" "),
        gender: a.gender,
        email: a.email,
        phone: a.mobileNumbers?.[0] ? `+${a.mobileNumbers[0]}` : "",
      })),
    });
  };

  // ===============================
  // Export to Excel
  // ===============================
  const handleExportExcel = () => {
    const dataForExcel = filteredAlumni.map((a, idx) => ({
      SN: idx + 1,
      "Alumni ID": a.alumniID,
      "Full Name": [a.title, a.firstName, a.middleName, a.lastName]
        .filter(Boolean)
        .join(" "),
      Gender: a.gender,
      Email: a.email,
      Telephone: a.mobileNumbers?.[0] ? `+${a.mobileNumbers[0]}` : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Alumni");
    XLSX.writeFile(workbook, "current_alumni.xlsx");
  };

  return (
    <div className="alumni-page">
      {/* USER INFO */}
      <div className="user-card">
        <div className="user-left">
          <div className="user-name">Welcome, {currentUser.fullname}</div>
          <div className="user-role">
            System Role: {currentUser.systemRole}
          </div>
        </div>
        <div className="user-right">
          <div className="user-date">{formatDate(serverTime.fullDateTime)}</div>
          <div className="user-time">{formatTime(serverTime.fullDateTime)}</div>
        </div>
      </div>

      <h1 className="page-title">Current Alumni Members</h1>

      {/* ACTION BUTTONS */}
      <div className="action-row">
        <button className="btn-primary" onClick={handlePrint}>
          <Printer size={16} /> Print
        </button>
        <button className="btn-secondary" onClick={handleExportExcel}>
          <FileSpreadsheet size={16} /> Export to Excel
        </button>
        <button className="btn-primary" onClick={handleGoToDeletedPage}>
          <Trash2 size={16} /> View Deleted Alumni
        </button>
      </div>

      {/* FILTERS */}
      <div className="filters">
        <select
          onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
        >
          <option value="">Gender</option>
          {genders.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>

        <select
          onChange={(e) => setFilters({ ...filters, year: e.target.value })}
        >
          <option value="">Year of Graduation</option>
          {getYearOptions(50).map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>

        <select
          onChange={(e) =>
            setFilters({ ...filters, associateSchool: e.target.value })
          }
        >
          <option value="">Associate School</option>
          {associateSchools.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          onChange={(e) =>
            setFilters({ ...filters, academicStatus: e.target.value })
          }
        >
          <option value="">Academic Status</option>
          {academicStatuses.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          onChange={(e) =>
            setFilters({ ...filters, professionalStatus: e.target.value })
          }
        >
          <option value="">Professional Status</option>
          {professionalStatuses.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <input
        className="search-input"
        placeholder="Search by Alumni ID, Full Name or Email"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* TABLE */}
      <div className="table-card">
        <div className="table-meta">
          <span>Total Alumni: {filteredAlumni.length}</span>
          <span>
            Page {page} of {totalPages}
          </span>
        </div>

        {loading ? (
          <div className="loading">Loading alumni records…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>SN</th>
                <th>Alumni ID</th>
                <th>Full Name</th>
                <th>Gender</th>
                <th>Telephone</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((a, idx) => {
                const fullName = [a.title, a.firstName, a.middleName, a.lastName]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <tr key={a._id}>
                    <td>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                    <td>{a.alumniID}</td>
                    <td>{fullName}</td>
                    <td>{a.gender}</td>
                    <td>{a.mobileNumbers?.[0] ? `+${a.mobileNumbers[0]}` : ""}</td>
                    <td className="actions">
                      <Eye size={16} onClick={() => handleView(a._id)} />
                      <Pencil size={16} onClick={() => handleEdit(a._id)} />
                      <Trash2 size={16} onClick={() => handleDelete(a._id)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* PAGINATION */}
        <div className="pagination">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>
            Previous
          </button>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}